import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { Comment } from "@/api";
import { ColumnModal, Dropdown } from "@/components";
import { DropdownOption } from "@/components/Forms/Dropdown";
import { Archive, Delete, Edit, Ellipsis } from "@/icons";
import { useTranslate, useUser } from "@/providers/context";
import { useDeleteComment } from "../hooks/useDeleteComment";
import { useCommentsContext } from "../providers/context/useCommentsContext";

const getConfirmationText = (isArchive: boolean, hasReplies: boolean) => {
  if (isArchive) {
    return hasReplies
      ? "The comment and its replies will be hidden from the app."
      : "The comment will be hidden from the app.";
  } else {
    return hasReplies
      ? "The comment and its replies will be permanently deleted."
      : "The comment will be permanently deleted.";
  }
};

const ConfirmDeleteModal = ({
  comment,
  deleteType,
  handleClose,
}: {
  comment: Comment;
  deleteType: "delete" | "archive" | undefined;
  handleClose: () => void;
}) => {
  const { translate } = useTranslate();
  const { mutate: deleteCommentMutate, isLoading: isDeleting } =
    useDeleteComment();
  const { itemId, sourceId } = useCommentsContext();

  const isArchive = deleteType === "archive";
  const hasReplies = comment.Responses.length > 0;
  const { SourceCommentId } = comment;

  const handleDelete = useCallback(async () => {
    deleteCommentMutate(
      {
        sourceCommentId: SourceCommentId,
        itemId: itemId,
        sourceItemId: sourceId,
        isArchive,
      },
      {
        onSuccess: () => {
          toast(
            <div className="flex items-center gap-2">
              <Delete className="h-6 w-6" />
              {`${translate("Comment")} ${translate(isArchive ? "archived" : "Deleted").toLowerCase()}`}
            </div>,
          );
          handleClose();
        },
        onError: (error) => {
          console.error(error);
          toast(translate("Something went wrong, please try again later."));
        },
      },
    );
  }, [
    itemId,
    sourceId,
    SourceCommentId,
    isArchive,
    deleteCommentMutate,
    translate,
    handleClose,
  ]);

  return (
    <ColumnModal
      open={!!deleteType}
      hideTrigger
      cols={3}
      title={
        isArchive
          ? translate("Are you sure you want to archive this comment?")
          : translate("Are you sure you want to delete this comment?")
      }
      primaryText={`${translate(isArchive ? "Archive" : "Delete")} ${translate("Comment")}`}
      danger
      confirmIsLoading={isDeleting}
      confirmDisabled={isDeleting}
      onConfirm={handleDelete}
      onCancel={handleClose}
    >
      <p className="p1-regular p-6 pt-0">
        {translate(getConfirmationText(isArchive, hasReplies))}
      </p>
    </ColumnModal>
  );
};

export const CommentDropdown = ({ comment }: { comment: Comment }) => {
  const { translate } = useTranslate();
  const { uid, loggedInUserIsAdmin } = useUser();
  const [deleteType, setDeleteType] = useState<"delete" | "archive">();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownOptions: DropdownOption[] = [];
  const { setCommentToEdit } = useCommentsContext();

  const isOwner = comment.SourceCustomerId === uid;

  if (isOwner) {
    dropdownOptions.push(
      {
        text: `${translate("Edit")} ${translate("Comment")}`,
        icon: <Edit className="h-4 w-4" />,
        onClick: () => {
          setCommentToEdit(comment);
          setDropdownOpen(false);
        },
      },
      {
        text: `${translate("Delete")} ${translate("Comment")}`,
        icon: <Delete className="h-4 w-4" />,
        color: "error",
        onClick: () => setDeleteType("delete"),
      },
    );
  }

  if (!isOwner && loggedInUserIsAdmin) {
    dropdownOptions.push({
      text: `${translate("Archive")} ${translate("Comment")}`,
      icon: <Archive className="h-4 w-4" />,
      color: "error",
      onClick: () => setDeleteType("archive"),
    });
  }

  if (dropdownOptions.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown
        open={dropdownOpen}
        className="min-w-[130px]"
        onOpenChange={setDropdownOpen}
        contentProps={{ align: "end" }}
        options={dropdownOptions}
        optionsClassName="capitalize"
      >
        <Ellipsis className="text-item-secondary" height={20} width={20} />
      </Dropdown>
      <ConfirmDeleteModal
        comment={comment}
        deleteType={deleteType}
        handleClose={() => setDeleteType(undefined)}
      />
    </>
  );
};
